import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import {
  types,
  BaseEvent,
  GetKioskLatestActivityRequest,
  KioskEvent,
  HardwareEvent,
  GetActivityLogRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';

export function* getActivityLogAsync({ payload }: GetActivityLogRequest) {
  const { page, kioskIds, userNames, startTime, endTime, sort, sortColumn, exportData = false } = payload;
  yield put({ type: types.GET_ACTIVITY_LOG.PENDING, payload: { pending: true, exportData } });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    let currentDate;
    let lastDate;
    if (startTime === null) {
      currentDate = new Date();
      currentDate.setDate(currentDate.getDate() - 90);
      currentDate = currentDate.toISOString();
    }
    if (endTime === null) {
      lastDate = new Date().toISOString();
    }

    const api = new API(`clients/${clientId}/activityLog?page=${page}`);
    const response: BaseEvent[] = yield call(api.create, {
      kioskIds,
      userNames,
      startTime: startTime ? startTime : currentDate,
      endTime: endTime ? endTime : lastDate,
      sort,
      sortColumn,
      limit: exportData ? null : '100'
    });

    if (!exportData && (page === 1 || page === undefined)) {
      yield put({ type: types.RESET_ACTIVITY_LOG });
    }

    yield put({ type: types.GET_ACTIVITY_LOG.SUCCESS, payload: { ...response, exportData } });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_ACTIVITY_LOG.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_ACTIVITY_LOG.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_ACTIVITY_LOG.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_ACTIVITY_LOG.PENDING, payload: { pending: false, exportData } });
  }
}

interface LatestEventsAPIResponse {
  kioskEvents: KioskEvent[];
  kioskHardwareEvents: HardwareEvent[];
}

export function* getKioskLatestActivityLogAsync({ payload }: GetKioskLatestActivityRequest) {
  yield put({ type: types.GET_KIOSK_LATEST_ACTIVITY_LOG.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const clientsApi = new API(`clients/${clientId}/kiosks/${payload.kioskId}/events/latest`);
    const response: LatestEventsAPIResponse = yield call(clientsApi.getAll);
    yield put({ type: types.GET_KIOSK_LATEST_ACTIVITY_LOG.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_KIOSK_LATEST_ACTIVITY_LOG.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_KIOSK_LATEST_ACTIVITY_LOG.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.GET_KIOSK_LATEST_ACTIVITY_LOG.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.GET_KIOSK_LATEST_ACTIVITY_LOG.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_ACTIVITY_LOG.REQUEST, getActivityLogAsync),
  takeLatest(types.GET_KIOSK_LATEST_ACTIVITY_LOG.REQUEST, getKioskLatestActivityLogAsync)
];
